import { useCallback, useState, useEffect, useRef } from 'react';
import {
  useInitialBoardLoad,
  useMoveCardMutation,
  useUpdateCardMutation,
  useReorderColumnsMutation,
  useDeleteCardMutation,
  useRenameColumnMutation,
  useArchiveColumnMutation,
  useLeadStagesQuery,
  usePracticeAreaQuery,
  useUsersQuery,
  useCreateLeadStageMutation,
  useLeadActionsMutation,
} from './queries';
import { useUserDetailsContext } from 'context/userDetailsContext';

export const useLazyBoardWithQuery = (filters = {}) => {
  const { userDetails } = useUserDetailsContext();
  const { practice_area = [], show_unassigned_pa_column = true } = userDetails?.user_preferences?.filters?.leads || {};
  // Use useRef for lock management to avoid race conditions and unnecessary rerenders
  const lockedEntitiesRef = useRef(new Set());
  const [lockedEntitiesVersion, setLockedEntitiesVersion] = useState(0); // Force rerenders when locks change

  const { practiceAreaList, isPracticeAreaLoading } = usePracticeAreaQuery({ loadFullList: true, status: 'all' });
  const { userList, isUserLoading } = useUsersQuery({ status: 'ACTIVE', excludeFromLoading: true });

  const {
    leadStages,
    isLeadStageLoading: isLoadingLeadStages,
    error: leadStagesError,
  } = useLeadStagesQuery(practice_area, show_unassigned_pa_column);

  const { isFetching: isRefetchingData, error: initialLoadError, refetch: resetBoard } = useInitialBoardLoad(leadStages, filters);

  const error = leadStagesError || initialLoadError;

  const moveCardMutation = useMoveCardMutation();
  const updateCardMutation = useUpdateCardMutation();
  const reorderColumnsMutation = useReorderColumnsMutation(practice_area, show_unassigned_pa_column);
  const deleteCardMutation = useDeleteCardMutation();
  const renameColumnMutation = useRenameColumnMutation(practice_area, show_unassigned_pa_column);
  const archiveColumnMutation = useArchiveColumnMutation(practice_area, show_unassigned_pa_column);
  const createLeadStageMutation = useCreateLeadStageMutation();
  const { removeLeadMutation, moveLeadToStageMutation, findClosedStage } = useLeadActionsMutation(leadStages);

  // Helper function to auto-reset mutation errors after a delay
  const useMutationErrorReset = (mutation, delay = 5000) => {
    useEffect(() => {
      if (mutation.isError) {
        const timer = setTimeout(() => mutation.reset(), delay);
        return () => clearTimeout(timer);
      }
    }, [mutation.isError, mutation, delay]);
  };

  // Auto-reset all mutation errors after 5 seconds
  useMutationErrorReset(moveCardMutation);
  useMutationErrorReset(updateCardMutation);
  useMutationErrorReset(reorderColumnsMutation);
  useMutationErrorReset(deleteCardMutation);
  useMutationErrorReset(renameColumnMutation);
  useMutationErrorReset(archiveColumnMutation);
  useMutationErrorReset(createLeadStageMutation);

  const moveCard = useCallback(
    async (dropData) => {
      const { sourceCardId } = dropData;
      const entityId = `task-${sourceCardId}`;

      // Check if entity is locked (atomic check with ref)
      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Card ${sourceCardId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      // Lock entity (atomic operation with ref)
      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates

      try {
        await moveCardMutation.mutateAsync({ ...dropData, filters });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        // Unlock entity after a short delay for UX
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates
        }, 500);
      }
    },
    [moveCardMutation, filters]
  );

  const reorderColumns = useCallback(
    async (reorderData) => {
      const { sourceIndex } = reorderData;

      if (!leadStages?.[sourceIndex]) {
        return { success: false, error: new Error('Column not found') };
      }

      const column = leadStages[sourceIndex];
      const entityId = `column-${column.id}`;

      // Prevent reordering of locked columns
      if (column.isLocked) {
        console.warn(`Column "${column.title}" is locked and cannot be reordered.`);
        return { success: false, locked: true };
      }

      // Check if entity is locked (atomic check with ref)
      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${column.id} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      // Lock entity (atomic operation with ref)
      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates

      try {
        await reorderColumnsMutation.mutateAsync({ reorderData, leadStages });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1); // Force rerender for UI updates
        }, 500);
      }
    },
    [leadStages, reorderColumnsMutation]
  );

  const updateCard = useCallback(
    async (cardId, columnId, updates) => {
      const entityId = `task-${cardId}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Card ${cardId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        // Pass data and filters to match mutation signature
        await updateCardMutation.mutateAsync({ cardId, columnId, data: updates, filters });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [updateCardMutation, filters]
  );

  const moveCardToStage = useCallback(
    async (cardId, sourceColumnId, targetColumnId, position = 'end') => {
      return await moveCard({
        sourceCardId: cardId,
        sourceColumnId,
        targetColumnId,
        position,
      });
    },
    [moveCard]
  );

  const renameColumn = useCallback(
    async (data) => {
      const entityId = `column-${data.sk}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${data.sk} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        await renameColumnMutation.mutateAsync(data);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [renameColumnMutation]
  );

  const archiveColumn = useCallback(
    async (columnId, pk) => {
      const entityId = `column-${columnId}`;

      if (lockedEntitiesRef.current.has(entityId)) {
        console.warn(`Column ${columnId} is currently syncing. Operation rejected.`);
        return { success: false, locked: true };
      }

      lockedEntitiesRef.current.add(entityId);
      setLockedEntitiesVersion((v) => v + 1);

      try {
        await archiveColumnMutation.mutateAsync({ columnId, pk });
        return { success: true };
      } catch (error) {
        return { success: false, error };
      } finally {
        setTimeout(() => {
          lockedEntitiesRef.current.delete(entityId);
          setLockedEntitiesVersion((v) => v + 1);
        }, 500);
      }
    },
    [archiveColumnMutation]
  );

  const createLeadStage = useCallback(
    async (data) => {
      try {
        await createLeadStageMutation.mutateAsync(data);
        return { success: true };
      } catch (error) {
        return { success: false, error };
      }
    },
    [createLeadStageMutation]
  );

  // Helper function to check if a stage name already exists
  const checkStageExists = useCallback(
    (stageName) => {
      if (!stageName || !leadStages) return false;
      const normalizedStageName = stageName.trim().toLowerCase();
      return leadStages.some((stage) => stage.lead_stage_name.toLowerCase() === normalizedStageName);
    },
    [leadStages]
  );

  /**
   * Remove a lead from the board (used after converting to case)
   * @param {string} leadId - The lead's sk
   * @param {string} columnId - The column the lead is currently in
   */
  const removeLead = useCallback(
    (leadId, columnId) => {
      removeLeadMutation.mutate({
        leadId,
        columnId,
        filters,
      });
    },
    [removeLeadMutation, filters]
  );

  /**
   * Move a lead to the closed stage (used after closing a lead)
   * @param {string} leadId - The lead's sk
   * @param {string} sourceColumnId - The column the lead is currently in
   */
  const moveLeadToClosedStage = useCallback(
    (leadId, sourceColumnId) => {
      const closedStage = findClosedStage();

      if (closedStage && sourceColumnId !== closedStage.sk) {
        moveLeadToStageMutation.mutate({
          leadId,
          sourceColumnId,
          targetColumnId: closedStage.sk,
          filters,
        });
      }
    },
    [moveLeadToStageMutation, findClosedStage, filters]
  );

  // Convert lockedEntities Set to Array for component compatibility
  const lockedEntitiesArray = Array.from(lockedEntitiesRef.current);

  return {
    practiceAreaList,
    isPracticeAreaLoading,
    userList,
    isLoadingUsers: isUserLoading,
    isLoadingLeadStages,
    initialLoading: isLoadingLeadStages,
    isRefetchingData, // Expose for showing skeleton loaders during filter changes
    error: error?.message || error || null,
    moveCard,
    reorderColumns,
    resetBoard,
    leadStages,
    // Operation status tracking with both pending and failed states
    operationStatus: new Map([
      // Move card operation
      ...(moveCardMutation.isLoading ? [['moveCard', { status: 'pending' }]] : []),
      ...(moveCardMutation.isError ? [['moveCard-error', { status: 'failed', error: moveCardMutation.error }]] : []),

      // Update card operation
      ...(updateCardMutation.isLoading ? [['updateCard', { status: 'pending' }]] : []),
      ...(updateCardMutation.isError ? [['updateCard-error', { status: 'failed', error: updateCardMutation.error }]] : []),

      // Reorder columns operation
      ...(reorderColumnsMutation.isLoading ? [['reorderColumns', { status: 'pending' }]] : []),
      ...(reorderColumnsMutation.isError ? [['reorderColumns-error', { status: 'failed', error: reorderColumnsMutation.error }]] : []),

      // Rename column operation
      ...(renameColumnMutation.isLoading ? [['renameColumn', { status: 'pending' }]] : []),
      ...(renameColumnMutation.isError ? [['renameColumn-error', { status: 'failed', error: renameColumnMutation.error }]] : []),

      // Archive column operation
      ...(archiveColumnMutation.isLoading ? [['archiveColumn', { status: 'pending' }]] : []),
      ...(archiveColumnMutation.isError ? [['archiveColumn-error', { status: 'failed', error: archiveColumnMutation.error }]] : []),

      // Create lead stage operation
      ...(createLeadStageMutation.isLoading ? [['createLeadStage', { status: 'pending' }]] : []),
      ...(createLeadStageMutation.isError ? [['createLeadStage-error', { status: 'failed', error: createLeadStageMutation.error }]] : []),

      // Remove lead operation (convert to case)
      ...(removeLeadMutation.isLoading ? [['removeLead', { status: 'pending' }]] : []),
      ...(removeLeadMutation.isError ? [['removeLead-error', { status: 'failed', error: removeLeadMutation.error }]] : []),

      // Move lead to closed stage operation
      ...(moveLeadToStageMutation.isLoading ? [['moveLeadToClosedStage', { status: 'pending' }]] : []),
      ...(moveLeadToStageMutation.isError
        ? [['moveLeadToClosedStage-error', { status: 'failed', error: moveLeadToStageMutation.error }]]
        : []),
    ]),
    hasPendingOperations: () =>
      moveCardMutation.isLoading ||
      updateCardMutation.isLoading ||
      reorderColumnsMutation.isLoading ||
      deleteCardMutation.isLoading ||
      renameColumnMutation.isLoading ||
      archiveColumnMutation.isLoading ||
      createLeadStageMutation.isLoading ||
      removeLeadMutation.isLoading ||
      moveLeadToStageMutation.isLoading,
    isEntityLocked: (entityId) => lockedEntitiesRef.current.has(entityId),
    lockedEntities: lockedEntitiesArray,
    // Card operations
    updateCard,
    moveCardToStage,
    // Column operations
    renameColumn,
    archiveColumn,
    createLeadStage,
    checkStageExists,
    removeLead,
    moveLeadToClosedStage,
  };
};
